import React, { useEffect } from 'react';
import SEO from '../SEO';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import LegalNavigation from '../LegalNavigation';

const PolitiqueConfidentialite = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Politique de Confidentialité (RGPD)"
        description="Politique de confidentialité SISENS - Protection des données personnelles et respect du RGPD. Découvrez comment nous protégeons vos informations."
        keywords="politique confidentialité, RGPD, protection données, SISENS, données personnelles"
        url="https://sisens.eu/politique-confidentialite"
      />
      {/* Navigation bar */}
      <LegalNavigation />
      
      <div className="pt-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Bouton retour */}
        <div className="mb-8">
          <Link 
            to="/"
            className="inline-flex items-center text-[#4b9fcc] hover:text-[#e71f74] transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </Link>
        </div>

        {/* Contenu de la politique de confidentialité */}
        <div className="prose prose-lg max-w-none">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            🔒 Politique de confidentialité (RGPD)
          </h1>
          <p className="text-sm text-gray-600 mb-8 italic">
            Mise à jour le 8 octobre 2025
          </p>

          {/* Section 1 - Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              1. Introduction
            </h2>
            <p className="mb-4">
              La société <span className="font-semibold">SISENS</span>, Société par actions simplifiée immatriculée au RCS de Lyon sous le numéro <span className="font-semibold">912 406 345</span>, dont le siège social est situé <span className="font-semibold">36 Allée des Prés Rouets, 69510 Messimy</span>, attache une grande importance à la protection de vos données personnelles et au respect de votre vie privée.
            </p>
            <p className="mb-4">
              La présente politique a pour objectif de vous informer de manière claire et transparente sur la manière dont SISENS collecte, utilise, protège et conserve vos données personnelles dans le cadre de l'utilisation du site <span className="font-semibold">https://www.sisens.eu</span>.
            </p>
          </section>

          {/* Section 2 - Responsable du traitement */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              2. Responsable du traitement
            </h2>
            <p className="mb-4">
              Le responsable du traitement des données personnelles collectées sur ce site est :
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="font-bold text-lg text-gray-900">SISENS</p>
              <p>📍 36 Allée des Prés Rouets, 69510 Messimy</p>
              <p>📧 <a href="mailto:contact@sisens.eu" className="text-[#4b9fcc] hover:text-[#e71f74]">contact@sisens.eu</a></p>
              <p className="mt-2"><span className="font-semibold">👤 Représentants légaux :</span> Laurent KELMANOWITZ et Christophe GARNIER</p>
            </div>
          </section>

          {/* Section 3 - Données personnelles collectées */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              3. Données personnelles collectées
            </h2>
            <p className="mb-4">
              Dans le cadre de votre utilisation du site, SISENS peut être amenée à collecter les données suivantes :
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 mb-4">
              <li><span className="font-semibold">Données d'identification :</span> nom, prénom, entreprise, fonction</li>
              <li><span className="font-semibold">Données de contact :</span> adresse e-mail, numéro de téléphone</li>
              <li><span className="font-semibold">Contenu des messages</span> envoyés via le formulaire de contact</li>
              <li><span className="font-semibold">Données de navigation :</span> adresse IP, type d'appareil, navigateur, pages consultées, durée de visite (via cookies analytiques)</li>
            </ul>
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <p className="text-[#4b9fcc] font-semibold">
                SISENS ne collecte que les données strictement nécessaires à la finalité poursuivie (principe de minimisation des données).
              </p>
            </div>
          </section>

          {/* Section 4 - Finalités du traitement */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              4. Finalités du traitement
            </h2>
            <p className="mb-4">
              Les données personnelles collectées sur le site <span className="font-semibold">https://www.sisens.eu</span> sont utilisées pour :
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 mb-4">
              <li>Répondre à vos demandes de contact ou d'information,</li>
              <li>Gérer la relation commerciale ou partenariale,</li>
              <li>Améliorer la qualité du site et mesurer sa fréquentation (via cookies analytiques),</li>
              <li>Assurer la sécurité du site et prévenir les tentatives de fraude.</li>
            </ul>
            <div className="bg-red-50 p-4 rounded-lg mb-4">
              <p className="text-red-700 font-semibold">
                Aucune donnée n'est utilisée à des fins commerciales sans votre consentement explicite.
              </p>
            </div>
          </section>

          {/* Section 5 - Base légale du traitement */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              5. Base légale du traitement
            </h2>
            <p className="mb-4">
              Conformément à l'article 6 du RGPD, le traitement des données repose sur les bases légales suivantes :
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 mb-4">
              <li><span className="font-semibold">Votre consentement</span> (formulaire de contact, cookies non essentiels),</li>
              <li><span className="font-semibold">L'intérêt légitime de SISENS</span> (sécurisation du site, amélioration du service),</li>
              <li><span className="font-semibold">L'exécution de mesures précontractuelles</span> (lorsqu'un échange précède une relation commerciale).</li>
            </ul>
          </section>

          {/* Section 6 - Durée de conservation des données */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              6. Durée de conservation des données
            </h2>
            <p className="mb-4">
              Vos données personnelles sont conservées pour une durée limitée :
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><span className="font-semibold">Formulaire de contact :</span> 12 mois à compter de la dernière prise de contact,</li>
                <li><span className="font-semibold">Cookies analytiques :</span> 13 mois maximum,</li>
                <li><span className="font-semibold">Données techniques (logs) :</span> 6 mois.</li>
              </ul>
            </div>
            <p>
              Au-delà de ces durées, les données sont supprimées ou anonymisées.
            </p>
          </section>

          {/* Section 7 - Destinataires des données */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              7. Destinataires des données
            </h2>
            <p className="mb-4">
              Les données collectées sont destinées uniquement à SISENS et à ses collaborateurs habilités à les traiter pour les finalités indiquées.
            </p>
            <div className="bg-green-50 p-4 rounded-lg mb-4">
              <p className="text-green-700 font-semibold mb-2">
                Elles ne sont jamais revendues ni transmises à des tiers à des fins commerciales.
              </p>
              <p className="text-green-700">
                Elles peuvent toutefois être hébergées ou traitées par des prestataires techniques (hébergeur, service de messagerie, maintenance web), soumis à une obligation stricte de confidentialité et de sécurité.
              </p>
            </div>
          </section>

          {/* Section 8 - Sécurité des données */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              8. Sécurité des données
            </h2>
            <p className="mb-4">
              SISENS met en œuvre toutes les mesures techniques et organisationnelles appropriées pour garantir la sécurité et la confidentialité de vos données personnelles, afin d'empêcher leur perte, altération, destruction ou accès non autorisé.
            </p>
            <p className="mb-4">
              Ces mesures incluent notamment :
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 mb-4">
              <li>le chiffrement des communications (protocole HTTPS),</li>
              <li>la protection des serveurs par pare-feu et antivirus,</li>
              <li>la gestion stricte des accès internes.</li>
            </ul>
          </section>

          {/* Section 9 - Vos droits */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              9. Vos droits
            </h2>
            <p className="mb-4">
              Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants sur vos données :
            </p>
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><span className="font-semibold">Droit d'accès</span> (obtenir la confirmation qu'elles sont traitées et en recevoir une copie)</li>
                <li><span className="font-semibold">Droit de rectification</span> (corriger ou compléter vos données)</li>
                <li><span className="font-semibold">Droit d'effacement</span> (supprimer vos données dans certains cas)</li>
                <li><span className="font-semibold">Droit d'opposition</span> (vous opposer à un traitement pour motif légitime)</li>
                <li><span className="font-semibold">Droit à la limitation du traitement</span></li>
                <li><span className="font-semibold">Droit à la portabilité</span> (recevoir vos données dans un format exploitable)</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="mb-2"><span className="font-semibold">Pour exercer vos droits, vous pouvez écrire à :</span></p>
              <p>📧 <a href="mailto:contact@sisens.eu" className="text-[#4b9fcc] hover:text-[#e71f74] font-semibold">contact@sisens.eu</a></p>
              <p className="mt-2 text-sm text-gray-600">SISENS s'engage à répondre à toute demande dans un délai maximal d'un mois.</p>
            </div>
            <p className="mb-4">
              Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la CNIL :
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p>📍 3 Place de Fontenoy – TSA 80715 – 75334 Paris Cedex 07</p>
              <p>🌐 <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#4b9fcc] hover:text-[#e71f74] underline">www.cnil.fr</a></p>
            </div>
          </section>

          {/* Section 10 - Cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              10. Cookies
            </h2>
            <p className="mb-4">
              Lors de la navigation sur notre site, des cookies peuvent être déposés sur votre terminal. Les cookies utilisés sont de trois types :
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 mb-4">
              <li><span className="font-semibold">Cookies strictement nécessaires :</span> indispensables au fonctionnement du site,</li>
              <li><span className="font-semibold">Cookies de mesure d'audience</span> (Google Analytics ou équivalent) : permettent d'analyser la fréquentation,</li>
              <li><span className="font-semibold">Cookies tiers :</span> liés à l'intégration de contenus externes (YouTube, réseaux sociaux, etc.).</li>
            </ul>
            <div className="bg-yellow-50 p-4 rounded-lg mb-4">
              <p className="mb-2"><span className="font-semibold text-yellow-800">Lors de votre première visite, un bandeau de consentement vous permet :</span></p>
              <ul className="list-disc list-inside ml-4 space-y-1 text-yellow-800">
                <li>d'accepter tous les cookies,</li>
                <li>de refuser tous les cookies,</li>
                <li>ou de personnaliser vos choix.</li>
              </ul>
            </div>
            <div className="bg-[#4b9fcc] text-white p-4 rounded-lg">
              <p className="font-semibold mb-2">🔧 Gestion de vos cookies</p>
              <p>
                Vous pouvez à tout moment modifier votre consentement en cliquant sur le lien <span className="font-semibold">"Gestion des cookies"</span> disponible en bas de page.
              </p>
            </div>
          </section>

          {/* Section 11 - Mise à jour de la politique */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              11. Mise à jour de la politique
            </h2>
            <p className="mb-4">
              SISENS se réserve le droit de modifier la présente politique de confidentialité à tout moment, afin de tenir compte de l'évolution de la législation, de la technologie ou de ses pratiques internes.
            </p>
            <p>
              La date de mise à jour figurant en haut de la page indique la dernière révision.
            </p>
          </section>

          {/* Résumé */}
          <section className="mb-8">
            <div className="bg-[#4b9fcc] text-white p-6 rounded-lg">
              <h2 className="text-2xl font-bold mb-4">
                ✅ En résumé :
              </h2>
              <ul className="list-disc list-inside space-y-2">
                <li>Vos données sont collectées uniquement pour répondre à vos demandes,</li>
                <li>Elles ne sont jamais revendues,</li>
                <li>Vous gardez le contrôle total sur leur utilisation.</li>
              </ul>
            </div>
          </section>
        </div>
      </div>
      </div>
    </div>
  );
};

export default PolitiqueConfidentialite;